export { isResolvedUri, toAbsoluteURI } from '@webex/ladon-ts';

/**
 * Helper to strip query params.
 *
 * @param url - The URL to strip query params from.
 * @returns The URL without query params.
 */
function stripQueryParams(url: string): string {
    try {
        const u = new URL(url);
        u.search = '';
        return u.toString();
    } catch {
        return url;
    }
}

/**
 * Fetches a JavaScript module from the given URL, caches it in the 'ladon-cache' 
 * Cache API if the URL is HTTP(S), and returns a Blob URL for use with APIs.
 * 
 * @param url - The URL of the JavaScript module to fetch.
 * @returns A promise that resolves to a Blob URL for the fetched module.
 */
export async function loadAndCacheFile(fileUrl: string): Promise<ArrayBufferLike | Uint8Array> {
    const cache = await caches.open('ladon-cache');

    // Normalize the URL by stripping query parameters
    const normalizedUrl = stripQueryParams(fileUrl);

    // Try to get the file from cache using its full URL
    const cachedResponse = await cache.match(normalizedUrl);
    if (cachedResponse) {
        return cachedResponse.arrayBuffer();
    }

    // If not in cache, fetch and cache the file
    const response = await fetch(fileUrl);

    // Ensure the response is valid
    if (!response.ok) {
        throw new Error(`Failed to fetch file: ${fileUrl}`);
    }

    const contentType = response.headers.get('Content-Type') || 'binary/octet-stream';

    // Validate expected MIME types
    if (!contentType.includes('application/wasm') && !contentType.includes('binary/octet-stream')) {
        throw new Error(`Unexpected MIME type: ${contentType} for ${normalizedUrl}`);
    }

    const fileBinary = await response.arrayBuffer();

    // Cache the response with proper headers
    const cachedResponseWithHeaders = new Response(fileBinary, {
        headers: {
            'Content-Type': contentType,
            'Content-Length': fileBinary.byteLength.toString(),
        },
    });

    // Store the response in the cache using the full URL as the key
    await cache.put(normalizedUrl, cachedResponseWithHeaders);

    return fileBinary;
}
